import { useState, useEffect, useCallback } from 'react';
import { Modal } from './Modal';
import { Input } from './Input';
import { Button } from './Button';
import { RichTextEditor } from './RichTextEditor';
import { FileText } from 'lucide-react';
import clsx from 'clsx';
import type { Band, EmailTemplate } from '../../types';
import { toEditableHtml, toStoredHtml } from '../../utils/emailImages';
import axios from 'axios';

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (templateData: TemplateFormData) => Promise<void>;
  template?: EmailTemplate | null;
  mode?: 'create' | 'edit';
  bands?: Band[];
}

export interface TemplateFormData {
  name: string;
  subject: string;
  email_text: string;
  band_ids: string[];
}

export function TemplateModal({ isOpen, onClose, onSubmit, template, mode = 'create', bands = [] }: TemplateModalProps) {
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    subject: '',
    email_text: '',
    band_ids: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Initialize form with template data in edit mode
  useEffect(() => {
    if (template && mode === 'edit') {
      setFormData({
        name: template.name,
        subject: template.subject,
        // cid: references → blob URLs, so the editor can display the images (docs/EMAIL_IMAGES.md)
        email_text: toEditableHtml(template.email_text || ''),
        band_ids: template.band_ids || [],
      });
    } else if (!isOpen || mode === 'create') {
      // Reset for create mode
      setFormData({
        name: '',
        subject: '',
        email_text: '',
        band_ids: [],
      });
    }
  }, [template, mode, isOpen]);

  const toggleBandSelection = (bandId: string) => {
    setFormData(prev => ({
      ...prev,
      band_ids: prev.band_ids.includes(bandId)
        ? prev.band_ids.filter(id => id !== bandId)
        : [...prev.band_ids, bandId],
    }));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError('Template name is required');
      return;
    }
    if (!formData.subject.trim()) {
      setError('Subject is required');
      return;
    }
    if (!formData.email_text.trim()) {
      setError('Template body is required');
      return;
    }

    try {
      setIsSubmitting(true);
      // ...and back to references on the way out. A template stores what the mail will send.
      await onSubmit({ ...formData, email_text: toStoredHtml(formData.email_text) });

      // Reset form only in create mode
      if (mode === 'create') {
        setFormData({
          name: '',
          subject: '',
          email_text: '',
          band_ids: [],
        });
      }
      onClose();
    } catch (err) {
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
      setError(detail || `Failed to ${mode} template`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        name: '',
        subject: '',
        email_text: '',
        band_ids: [],
      });
      setError('');
      onClose();
    }
  };

  // Use useCallback to prevent re-renders
  const handleBodyChange = useCallback((value: string) => {
    setFormData(prev => ({ ...prev, email_text: value }));
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === 'edit' ? 'Edit Template' : 'Create New Template'}
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            isLoading={isSubmitting}
            onClick={() => handleSubmit()}
          >
            {mode === 'edit' ? 'Update Template' : 'Create Template'}
          </Button>
        </>
      }
    >
      <div className="space-y-6 w-full">
        {error && (
          <div className="p-4 bg-red-900/20 border border-red-700/50 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Template Name and Associated Bands */}
        <div className="flex gap-4 w-full">
          <div className="flex-1 min-w-0">
            <Input
              label="Template Name *"
              type="text"
              placeholder="e.g., Initial Contact, Follow-up Email"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              icon={<FileText className="w-4 h-4" />}
              containerClassName="w-full"
            />
          </div>

          {/* Associated Bands */}
          {bands.length > 0 && (
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-dark-text-secondary mb-1">
                Associated Bands
              </label>
              <div className="flex flex-wrap gap-2">
                {bands.map((band) => (
                  <button
                    key={band._id}
                    type="button"
                    onClick={() => toggleBandSelection(band._id)}
                    className={clsx(
                      'px-3 py-1.5 rounded-full text-xs font-medium transition-all',
                      formData.band_ids.includes(band._id)
                        ? 'bg-primary-900/30 text-primary-400 border border-primary-700/50'
                        : 'bg-dark-elevated text-dark-text-secondary border border-dark-border hover:bg-dark-border'
                    )}
                  >
                    {formData.band_ids.includes(band._id) ? '✓ ' : '+ '}
                    {band.name}
                  </button>
                ))}
              </div>
              {formData.band_ids.length === 0 && (
                <div className="mt-2 text-xs text-dark-text-muted italic">
                  No bands selected - generic template
                </div>
              )}
            </div>
          )}
        </div>

        {/* Subject Line */}
        <div className="w-full">
          <Input
            label="Email Subject *"
            type="text"
            placeholder="e.g., Booking Inquiry for {{venue_name}}"
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            required
            containerClassName="w-full"
          />
        </div>

        {/* Email Body */}
        <div className="w-full">
          <label className="block text-sm font-medium text-dark-text mb-3">
            Email Body *
          </label>
          <RichTextEditor
            content={formData.email_text}
            onChange={handleBodyChange}
            placeholder="Write your email template here..."
          />
        </div>
      </div>
    </Modal>
  );
}
